import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { MigrationCtx } from '@/lib/migration/useMigration'
import type { IncompleteSection } from '@/lib/migration/submission'
import type { SubmissionMarked } from '@/api/types/migration'
import { RdField, RdModal } from '@/components/readiness/RdModal'
import { Icon } from '@/components/readiness/primitives'
import { uid } from '@/lib/readiness/util'

// ── Mark as submitted (Iteration 3 §7) ──────────────────────────────────────
// Records who submitted v{n} to CSA and when, then freezes it. Step-up is a
// re-authentication, not a typed name: the platform's password check, or the
// IdP's `prompt=login` for SSO tenants (Note 6). The password is checked for
// presence here; qtz-readiness names the signed-in user on the record and
// stores the portal's report of the step-up with the event.

const SSO_IDP = import.meta.env.VITE_SSO_IDP || null
const localDateTime = (d = new Date()) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)

export function MarkSubmittedModal({ ctx, openChecks, incomplete = [], onClose, onDone }: { ctx: MigrationCtx; openChecks: string[]; incomplete?: IncompleteSection[]; onClose: () => void; onDone: (rec: SubmissionMarked) => void }) {
  const { actor, plan: data, actions, indexId } = ctx
  const fixTo = (i: IncompleteSection) => ('annex' in i.to ? `/readiness/submission/plan?index=${indexId}&annex=${i.to.annex}` : `/readiness/submission/plan?index=${indexId}&part=a${i.to.section ? `#${i.to.section}` : ''}`)
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  const [when, setWhen] = useState(localDateTime())
  const [ref, setRef] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [note, setNote] = useState('')
  const [confirm, setConfirm] = useState(false)
  const [password, setPassword] = useState('')
  const [ssoVerified, setSsoVerified] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const n = data.plan?.plan_state.n ?? 1
  const authed = SSO_IDP ? ssoVerified : password.length > 0
  const can = confirm && authed && !!when && !busy
  const initials = actor.slice(0, 2).toUpperCase()

  const submit = async () => {
    if (!can) return
    setBusy(true)
    setError(null)
    try {
      const rec = await actions.markSubmitted({
        user_id: actor,
        submitted_at: new Date(when).toISOString(),
        time_zone: tz,
        csa_reference: ref.trim() || null,
        acknowledgement_id: file ? uid('ack') : null,
        acknowledgement_name: file?.name ?? null,
        // The incomplete sections travel with the record, as the open checks do.
        open_checks: [...openChecks, ...incomplete.map((i) => i.text)],
        note: note.trim() || null,
        reauth: SSO_IDP ? 'sso' : 'password',
        auth_time: new Date().toISOString(),
      })
      onDone(rec)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <RdModal
      title="Mark as submitted"
      width={720}
      onClose={onClose}
      footer={
        <div className="flex w-full flex-wrap items-center gap-3">
          <p className="mr-auto max-w-[400px] text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>Writes an audit event: user, time, IP, open checks. v{n} becomes read-only; a new version can be started later.</p>
          <button type="button" className="rd-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="rd-btn rd-btn-primary" disabled={!can} onClick={() => void submit()} data-testid="confirm-mark-submitted">{busy ? 'Recording…' : 'Mark as submitted'}</button>
        </div>
      }
    >
      <p className="-mt-1 mb-4 text-[14px]" style={{ color: 'var(--rd-text-muted)' }}>Records who submitted v{n} to CSA and when, then freezes it. Export stays available at any time and is not required for this step.</p>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <RdField label="Submitted on" htmlFor="ms-when" hint={`Time zone ${tz}`} required>
          <input id="ms-when" type="datetime-local" className="rd-input" value={when} onChange={(e) => setWhen(e.target.value)} />
        </RdField>
        <RdField label="Submitted by" htmlFor="ms-by" hint="From your sign-in">
          <div id="ms-by" className="rd-input flex items-center gap-2" aria-readonly="true">
            <span className="grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold text-white" style={{ background: 'var(--rd-accent-primary)' }}>{initials}</span>
            <span className="truncate">{actor}</span>
          </div>
        </RdField>
        <RdField label="CSA reference (optional)" htmlFor="ms-ref">
          <input id="ms-ref" className="rd-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. CSA-QSM-2026-0418" />
        </RdField>
        <RdField label="Acknowledgement (optional)" htmlFor="ms-ack" hint={file ? file.name : 'The acknowledgement CSA sent back, if any'}>
          <input id="ms-ack" type="file" className="rd-input" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </RdField>
      </div>
      <RdField label="Note (optional)" htmlFor="ms-note">
        <textarea id="ms-note" className="rd-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </RdField>

      {incomplete.length > 0 && (
        <div className="mb-3 rounded-[10px] px-4 py-3 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-text-secondary)', border: '1px solid var(--rd-status-warn)' }} data-testid="incomplete-sections" role="alert">
          <p className="mb-1 flex items-center gap-1.5 font-semibold" style={{ color: 'var(--rd-status-warn)' }}><Icon name="warn" size={14} /> {incomplete.length} incomplete {incomplete.length === 1 ? 'section' : 'sections'} · the plan is not ready for CSA as it stands</p>
          <p className="mb-1.5" style={{ color: 'var(--rd-text-muted)' }}>The export would refuse these. Fix them before submitting, or record the submission with them noted.</p>
          <ul className="max-h-48 space-y-1 overflow-y-auto pr-1">
            {incomplete.map((i) => (
              <li key={i.key} className="flex items-start gap-2">
                <span className="min-w-0 flex-1">{i.text}</span>
                <Link to={fixTo(i)} className="rd-link shrink-0" onClick={onClose}>Fix →</Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {openChecks.length > 0 && (
        <div className="mb-3 rounded-[10px] px-4 py-3 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)', color: 'var(--rd-text-secondary)' }} data-testid="open-checks">
          <p className="mb-1 flex items-center gap-1.5 font-semibold" style={{ color: 'var(--rd-status-warn)' }}><Icon name="warn" size={14} /> Open checks · recorded with the submission, never blocking</p>
          <ul className="list-disc pl-5">{openChecks.map((c) => <li key={c}>{c}</li>)}</ul>
        </div>
      )}
      <label className="mb-4 flex items-start gap-2 text-[14px]">
        <input type="checkbox" className="mt-0.5" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="confirm-as-it-stands" />
        <span>I confirm the plan was submitted to CSA as it stands{incomplete.length ? `, with the ${incomplete.length} incomplete ${incomplete.length === 1 ? 'section' : 'sections'} above` : openChecks.length ? ', with the open checks noted above' : ''}</span>
      </label>
      {SSO_IDP ? (
        <button type="button" className="rd-btn" onClick={() => setSsoVerified(true)} disabled={ssoVerified}>{ssoVerified ? `Verified with ${SSO_IDP}` : `Verify with ${SSO_IDP}`}</button>
      ) : (
        <RdField label="Confirm with your password" htmlFor="ms-pw" required hint="Re-authentication; a typed name is not accepted">
          <input id="ms-pw" type="password" autoComplete="current-password" className="rd-input" value={password} onChange={(e) => setPassword(e.target.value)} />
        </RdField>
      )}
      {error && <p className="mt-2 text-[12.5px]" style={{ color: 'var(--rd-status-warn)' }} role="alert">{error}</p>}
    </RdModal>
  )
}

export function SetDateModal({ ctx, onClose }: { ctx: MigrationCtx; onClose: () => void }) {
  const { plan: data, actions, summary } = ctx
  const [date, setDate] = useState((data.plan?.plan_state.submission_date ?? summary?.due?.date ?? new Date().toISOString()).slice(0, 10))
  return (
    <RdModal title="Set submission date" onClose={onClose} footer={<><button type="button" className="rd-btn" onClick={onClose}>Cancel</button><button type="button" className="rd-btn rd-btn-primary" disabled={!date} onClick={() => { void actions.setSubmissionDate(date); onClose() }} data-testid="confirm-submission-date">Confirm date</button></>}>
      <RdField label="Planned submission date" htmlFor="sd-date" hint={summary?.due ? `CSA deadline ${summary.due.date}` : undefined}>
        <input id="sd-date" type="date" className="rd-input" value={date} onChange={(e) => setDate(e.target.value)} />
      </RdField>
    </RdModal>
  )
}
